import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { useToastContext } from "@/common/components/ui/toast/toast.context";
import Loading from "@/common/components/ui/loading/Loading";
import GenericTable from "@/common/components/ui/table/GenericTable";
import {
  TableFilters,
  TableFiltersState,
} from "@/common/components/ui/table/TableFilters";
import TableFooter from "@/common/components/ui/table/TableFooter";

import {
  IPartnerPreparationReport,
  IWorkOrderWithPreparation,
} from "@/types/workorder.types";

import { formatDate } from "@/common/helpers/date.helpers";
import { getPartnerPreparationReport } from "@/common/services/workOrder.api";
import VehiclePreparationStatus from "@/modules/vehicles/components/VehiclePreparationStatus";
import { IVehicle } from "@/types/vehicle.type";
import ModalPreparationVehicle from "@/modules/vehicles/components/modals/ModalPreparationVehicle";

const STATUS_OPTIONS = {
  opened: "Abertas",
  released: "Liberadas",
};

const work_order_type = {
  preparation: "Preparação",
  repreparation: "Repreparação",
};

const headers = {
  createdAt: "Data",
  licensePlate: "Placa",
  mechanic: "Mecânico",
  type: "Tipo",
  preparation: "Preparação",
  status: "Status",
};

function DashboardCards({
  partnerName,
  data,
}: {
  partnerName: string;
  data: IPartnerPreparationReport["dashboard"];
}) {
  return (
    <div className="mb-6">
      <h2 className="text-2xl font-semibold text-gray-700 mb-4">
        Relatório do Parceiro:{" "}
        <span className="text-blue-600">{partnerName}</span>
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-md text-center">
          <p className="text-gray-500 text-sm">OS Liberadas</p>
          <p className="text-2xl font-bold text-green-600">
            {data.releasedCount}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-md text-center">
          <p className="text-gray-500 text-sm">OS Abertas</p>
          <p className="text-2xl font-bold text-yellow-600">
            {data.openedCount}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-md text-center">
          <p className="text-gray-500 text-sm">Faturadas ao Parceiro</p>
          <p className="text-2xl font-bold text-blue-600">{data.billedCount}</p>
        </div>
      </div>
    </div>
  );
}

export function PreparationReportsDetailsByPartner() {
  const { companyName } = useParams<{ companyName: string }>();
  const { notifyError } = useToastContext();
  const [selectedWorkOrder, setSelectedWorkOrder] =
    useState<IWorkOrderWithPreparation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [reportData, setReportData] =
    useState<IPartnerPreparationReport | null>(null);
  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
  });
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (!companyName) {
      setIsLoading(false);
      return;
    }

    async function loadReport() {
      setIsLoading(true);
      try {
        const result = await getPartnerPreparationReport(companyName as string);
        if (result.ok && result.data) {
          setReportData(result.data);
        } else {
          notifyError(
            result.message || "Erro ao buscar relatório do parceiro.",
          );
        }
      } catch (error) {
        notifyError("Ocorreu um erro inesperado.");
      } finally {
        setIsLoading(false);
      }
    }

    loadReport();
  }, [companyName, notifyError]);

  const filteredWorkOrders = useMemo(() => {
    if (!reportData) return [];

    return reportData.workOrders.filter((wo) => {
      const searchMatch = wo.licensePlate
        .toLowerCase()
        .includes(filters.search.toLowerCase());
      const statusMatch =
        filters.status.length === 0 || filters.status.includes(wo.status);
      return searchMatch && statusMatch;
    });
  }, [reportData, filters]);

  const paginatedWorkOrders = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    return filteredWorkOrders.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredWorkOrders, page, itemsPerPage]);

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  if (isLoading) {
    return <Loading />;
  }

  if (!reportData) {
    return (
      <div className="text-center p-8 text-gray-500">
        Nenhum dado encontrado para este parceiro.
      </div>
    );
  }

  const actions = [
    {
      label: "Visualizar Preparação",
      action: (data: unknown) => {
        const wo = data as IWorkOrderWithPreparation;
        setSelectedWorkOrder(wo); // Isso ativa o modal passando o WorkOrder com seu _id!
      },
    },
  ];

  return (
    <>
      <div className="table-section">
        <DashboardCards
          partnerName={companyName!}
          data={reportData.dashboard}
        />

        <TableFilters
          dashboardHeader="Ordens de Serviço de Preparação"
          placeholder="Pesquisar por Placa"
          statusOptions={STATUS_OPTIONS}
          onSearch={handleFilterChange}
          tableData={filteredWorkOrders}
        />

        <GenericTable
          headers={headers}
          data={paginatedWorkOrders}
          actions={actions}
          subtitles={{
            preparation: (
              <>
                <span
                  role="PP"
                  className="px-1 cursor-help hover:bg-gray-200"
                  title="PPF"
                >
                  PP
                </span>
                <span
                  role="RT"
                  className="px-1 cursor-help hover:bg-gray-200"
                  title="Rastreador"
                >
                  RT
                </span>
                <span
                  role="SC"
                  className="px-1 cursor-help hover:bg-gray-200"
                  title="Suporte Celular"
                >
                  SC
                </span>
                <span
                  role="CC"
                  className="px-1 cursor-help hover:bg-gray-200"
                  title="Carregador Celular"
                >
                  CC
                </span>
                <span
                  role="PC"
                  className="px-1 cursor-help hover:bg-gray-200"
                  title="Protetor Carenagem"
                >
                  PC
                </span>
                <span
                  role="VR"
                  className="px-1 cursor-help hover:bg-gray-200"
                  title="Verificação Rastreador"
                >
                  VR
                </span>
                <span
                  role="CV"
                  className="px-1 cursor-help hover:bg-gray-200"
                  title="CRLV"
                >
                  CV
                </span>
                <span
                  role="NF"
                  className="px-1 cursor-help hover:bg-gray-200"
                  title="Nota Fiscal"
                >
                  NF
                </span>
                <span
                  role="PV"
                  className="px-1 cursor-help hover:bg-gray-200"
                  title="Proteção Veicular"
                >
                  PV
                </span>
                <span
                  role="CR"
                  className="px-1 cursor-help hover:bg-gray-200"
                  title="Chave Reserva"
                >
                  CR
                </span>
              </>
            ) as unknown as string,
          }}
          renders={{
            createdAt: (date) => formatDate(date as Date),
            status: (statusValue: unknown) => {
              const statusKey = statusValue as keyof typeof STATUS_OPTIONS;
              return STATUS_OPTIONS[statusKey] || String(statusValue);
            },
            type: (typeValue: unknown) => {
              const statusKey = typeValue as keyof typeof work_order_type;
              return work_order_type[statusKey] || String(typeValue);
            },

            preparation: (_, row: unknown) => {
              const wo = row as IWorkOrderWithPreparation;
              return (
                <div
                  className="cursor-pointer"
                  onClick={() => setSelectedWorkOrder(wo)}
                >
                  <VehiclePreparationStatus
                    vehiclePreparing={
                      wo.vehiclePreparing as IVehicle["vehiclePreparing"]
                    }
                  />
                </div>
              );
            },
          }}
          entity="partner-preparation"
        />

        <TableFooter
          page={page}
          totalItems={filteredWorkOrders.length}
          itemsPerPage={itemsPerPage}
          goToPage={setPage}
        />
      </div>
      {selectedWorkOrder && (
        <ModalPreparationVehicle
          onClose={() => setSelectedWorkOrder(null)}
          isReadOnly={true}
          type={selectedWorkOrder.type}
          workOrderId={selectedWorkOrder._id}
          vehicle={
            {
              _id: selectedWorkOrder.vehicleId || "",
              licensePlate: selectedWorkOrder.licensePlate,
              model: selectedWorkOrder.model || "",
              vehiclePreparing: selectedWorkOrder.vehiclePreparing,
            } as IVehicle
          }
        />
      )}
    </>
  );
}
